import { Fragment, type MouseEvent, type ReactNode } from 'react'
import { useIntl } from 'react-intl'

import { EllipsisVerticalIcon } from '@gto/icons-react/outline/ellipsis-vertical'
import { IconButton } from '@gto/ui/icon-button'
import { Menu } from '@gto/ui/menu'

export type RowAction = {
  label: ReactNode
  icon?: ReactNode
  color?: string
  disabled?: boolean
  onClick: () => void
  dividerBefore?: boolean
}

type RowActionsMenuProps = {
  actions: RowAction[]
}

export const RowActionsMenu = ({ actions }: RowActionsMenuProps) => {
  const { formatMessage } = useIntl()

  return (
    <Menu.Root>
      <Menu.Trigger
        render={
          <IconButton
            aria-label={formatMessage({ id: 'common.rowActions' })}
            size="small"
            // Stop the click bubbling to the surrounding table row.
            onClick={(e: MouseEvent<HTMLElement>) => e.stopPropagation()}
          >
            <EllipsisVerticalIcon
              height={16}
              width={16}
            />
          </IconButton>
        }
      />
      <Menu.Portal>
        <Menu.Positioner align="end">
          <Menu.Popup className="min-w-[160px]">
            <Menu.Group>
              {actions.map((action, i) => {
                const key = typeof action.label === 'string' ? action.label : `action-${i}`

                return (
                  <Fragment key={key}>
                    {action.dividerBefore === true && <Menu.Separator />}
                    <Menu.Item
                      disabled={action.disabled}
                      // Inline color (e.g. destructive red) wins over the base
                      // recipe's hover tint via style-specificity.
                      style={action.color == null ? undefined : { color: action.color }}
                      onClick={action.onClick}
                    >
                      {action.icon}
                      {action.label}
                    </Menu.Item>
                  </Fragment>
                )
              })}
            </Menu.Group>
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  )
}
